import { useBoolean } from "@chakra-ui/react";
import axios from "axios";
import { useStore } from "commons/hooks";
import { callAddMigrationScript, callRetrieveMigrationScript, getMigrationScriptContent, GetMigrationScriptParams, MigrationScriptFileDetail, MigrationScriptRequest, MigrationScriptResponse } from "data/Migrate";
import useLoader from "hooks/useLoader";
import JSZip from "jszip";
import { useState } from "react";
import Swal from "sweetalert2";

function MigrationPageViewModel() {
    const { authentication } = useStore();
    const citizenId = authentication.masterEmployeeResponse?.ctizenID;
    const nickName = authentication.masterEmployeeResponse?.nickName;
    const screenLoader = useLoader();

    const companyList: { value: string; name: string }[] = [
        { name: 'DTC', value: 'dtc' }
    ];
    const headers: string[] = [
        'Company',
        'File Name',
        'Type',
        'TK',
        'Detail',
        'Remark',
        'Develop',
        'Content'
    ]

    const [showAddMigrationScriptModal, setShowAddMigrationScriptModal] = useBoolean(false);
    const [showScriptDetail, setShowScriptDetail] = useBoolean(false);

    const [searchHelp, setSearchHelp] = useState<GetMigrationScriptParams>({
        company: '',
        develop: '',
        fileName: '',
        tk: '',
        type: ''
    });
    const [selectedFileDetail, setSelectedFileDetail] = useState<MigrationScriptFileDetail>({
        s3_file_path: '',
        s3_guid: ''
    });
    const [selectedFileContent, setSelectFileContent] = useState<string>('');

    const handleSearchChange = <K extends keyof GetMigrationScriptParams>(
        key: K,
        value: GetMigrationScriptParams[K]
    ) => {
        setSearchHelp(prevState => ({
            ...prevState,
            [key]: value
        }));
    };

    const [migrationScriptList, setMigrationScriptList] = useState<MigrationScriptResponse[]>()

    const handleConfirmAddMigrationScript = async (req: MigrationScriptRequest) => {
        try {
            screenLoader.show();
            req.created_by = citizenId ?? nickName!;
            if (
                req.files.length === 0
            ) {
                const swalAction = await Swal.fire(
                    'Create TR',
                    'Attach files not found.',
                    'warning'
                );

                if (swalAction.isConfirmed) {
                    return;
                }
            }

            await callAddMigrationScript(req);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error', error.message, 'error');
            }
        } finally {
            searchHelp.company = req.company;
            searchHelp.type = req.type;
            screenLoader.hide();
            fetchDataMigrationScript();
        }
    };

    const fetchDataMigrationScript = async () => {
        try {
            screenLoader.show();
            const response = await callRetrieveMigrationScript(searchHelp);

            if (response.status === 200) {
                setMigrationScriptList(response.data);
            }
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error', error.message, 'error');
            }
        } finally {
            screenLoader.hide();
        }
    }

    const fetchFileContent = async (fileContent: MigrationScriptFileDetail) => {
        try {
            screenLoader.show();

            const response = await getMigrationScriptContent(fileContent.s3_file_path);
            setSelectFileContent(response);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error', error.message, 'error');
            }
        }
        finally {
            screenLoader.hide();
        }
    };

    const handleOpenScriptFileDetail = (filePath: MigrationScriptFileDetail) => {
        setSelectFileContent("");
        fetchFileContent(filePath);

        setShowScriptDetail.on();
    }

    const handleDownloadFile = async () => {
        try {
            screenLoader.show();

            const zipBlob = await createZipFromFiles();

            if (zipBlob.size === 0) {
                return;
            } else {
                downloadBlob(zipBlob, searchHelp.company + searchHelp.type);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: (error as any).message
            })
        } finally {
            screenLoader.hide();
        }
    }

    const createZipFromFiles = async (): Promise<Blob> => {
        const zip = new JSZip();

        for (const entity of migrationScriptList!) {
            if (!entity.s3_file_path) {
                continue;
            }

            const folder = zip.folder(entity.group);

            try {
                const fileData = await downloadFile(entity.s3_file_path);

                if (fileData) {
                    folder?.file(entity.file_name, fileData);
                }
            } catch (error) {
                throw error;
            }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });

        return zipBlob;
    }

    const downloadFile = async (url: string): Promise<Blob | undefined> => {
        try {
            const response = await axios.get(url, { responseType: 'blob' });

            return response.data;
        } catch {
            return undefined;
        }
    }

    const downloadBlob = (blob: Blob, filename: string) => {
        if (!blob || blob.size === 0) {
            return;
        }

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    return {
        headers,
        searchHelp,
        companyList,
        showScriptDetail,
        selectedFileDetail,
        selectedFileContent,
        migrationScriptList,
        showAddMigrationScriptModal,

        handleSearchChange,
        setShowScriptDetail,
        fetchDataMigrationScript,
        handleOpenScriptFileDetail,
        handleDownloadFile,
        setShowAddMigrationScriptModal,
        handleConfirmAddMigrationScript
    }
}

export default MigrationPageViewModel;